import { useState } from 'react'
import { startZohoAuth, disconnectZoho, refreshZohoConnection } from '../api/settings'
import { useDeskOrg } from '../hooks/useDeskOrg'
import { useZohoCallbackResult } from '../hooks/useZohoCallbackResult'
import { clearZohoCallbackResult } from '../lib/zohoResult'
import type { MeResponse } from '../types'
import type { OrgSettings } from '../api/orgSettings'

const DCS = ['com', 'in', 'eu', 'com.au']

/**
 * Zoho refuses to render its consent screen in an iframe, so from the widget
 * this always opens a new tab. useAppState re-checks on window focus, so
 * returning to the ticket picks up the new connection without a manual refresh.
 */
export function ZohoSection({
  me,
  desk,
  compact,
  onChanged,
}: {
  me: MeResponse | null
  /** Who the Zoho connection belongs to, as the help desk sees them. */
  desk: OrgSettings['deskUser'] | null
  compact?: boolean
  onChanged: () => void
}) {
  const [dc, setDc] = useState(me?.zoho.dc ?? 'com')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [access, setAccess] = useState<{ canWrite: boolean; message: string } | null>(null)

  const ok = me?.zoho.valid

  // The widget says which help desk it is open in; the connection says which
  // one it is for. When both are known and they differ, every ticket call will
  // fail, so say it here instead of letting the first draft report a missing
  // ticket.
  const here = useDeskOrg()
  const connectedTo = me?.zoho.orgId ? String(me.zoho.orgId) : ''
  const wrongPortal =
    useZohoCallbackResult() === 'wrong_portal' ||
    Boolean(ok && here && connectedTo && here !== connectedTo)

  async function connect() {
    setBusy(true)
    setError('')
    clearZohoCallbackResult()
    try {
      const { url } = await startZohoAuth(dc)
      if (compact) window.open(url, '_blank', 'noopener')
      else window.location.assign(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // A cached token is invisible while it works for reads and fails only on the
  // one thing a new scope was added for. This throws it away and says what the
  // connection is actually allowed to do, without sending anything.
  async function refresh() {
    setBusy(true)
    setError('')
    try {
      setAccess(await refreshZohoConnection())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    setBusy(true)
    try {
      await disconnectZoho()
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Zoho account {ok && <span className="ok">✓</span>}</h2>
      <p className="hint">Reads tickets and posts replies as you.</p>

      {error && <div className="error">{error}</div>}

      {wrongPortal && (
        <div className="error">
          Wrong help desk. Press Disconnect, then Connect, and on Zoho's screen pick the help
          desk you are working in.
        </div>
      )}

      {ok ? (
        <>
          <dl>
            <dt>Help desk</dt>
            <dd>{me?.zoho.orgName || me?.zoho.orgId || 'Checking…'}</dd>
            <dt>Data centre</dt>
            <dd>zoho.{me?.zoho.dc}</dd>
            {/* The Zoho account this connection was authorised with, which
                need not be whoever is signed into the panel. Shown because in
                a browser where two people have connected in turn, nothing
                else on the screen says which one Desk is answering for. */}
            {desk?.email && (
              <>
                <dt>Connected as</dt>
                <dd>{desk.email}</dd>
              </>
            )}
            <dt>Desk profile</dt>
            <dd>
              {desk?.profile || 'Checking…'}
              {desk?.profile && (desk.admin ? ' · can manage the team' : ' · their own only')}
            </dd>
          </dl>

          {access && (
            <p className="note">
              {access.canWrite ? '✓ ' : '⚠ '}
              {access.message}
            </p>
          )}

          <button className="secondary" disabled={busy} onClick={refresh}>
            {busy ? 'Checking…' : 'Refresh connection'}
          </button>
          <button className="secondary" disabled={busy} onClick={disconnect}>
            Disconnect
          </button>
        </>
      ) : (
        <>
          <label className="field">
            <span>Data centre</span>
            <select value={dc} onChange={(e) => setDc(e.target.value)}>
              {DCS.map((d) => (
                <option key={d} value={d}>
                  zoho.{d}
                </option>
              ))}
            </select>
          </label>
          <button disabled={busy} onClick={connect}>
            {busy ? 'Opening…' : compact ? 'Connect Zoho ↗' : 'Connect Zoho'}
          </button>
          {compact && <p className="note">Opens a new tab. Zoho cannot show its login inside Desk.</p>}
        </>
      )}
    </div>
  )
}
